import { collegeTeams } from '../data/teams';
import { getFootballSeasonYear } from './season';

const CACHE_TTL_MS = 30 * 60 * 1000;
const cache = new Map();

const isCollegeTeam = (teamName) => collegeTeams.some(team => team.name === teamName);

const getCached = (key) => {
  const value = cache.get(key);
  if (!value) return null;
  if (Date.now() - value.savedAt > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return value.data;
};

const putCached = (key, data) => {
  cache.set(key, { savedAt: Date.now(), data });
};

const todayEastern = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find(part => part.type === 'year')?.value;
  const month = parts.find(part => part.type === 'month')?.value;
  const day = parts.find(part => part.type === 'day')?.value;
  return `${year}-${month}-${day}`;
};

class CFBDScheduleAPI {
  static supportsTeam(teamName) {
    return isCollegeTeam(teamName);
  }

  static async getTeamSchedule(teamName) {
    if (!this.supportsTeam(teamName)) return null;

    const season = getFootballSeasonYear();
    const key = `${season}:${teamName}`;
    const cached = getCached(key);
    if (cached) return cached;

    const params = new URLSearchParams({
      team: teamName,
      season: String(season),
    });

    const response = await fetch(`/api/cfbd-schedule?${params.toString()}`);
    let data = {};
    try {
      data = await response.json();
    } catch {
      // Preserve HTTP status below as the useful signal.
    }

    if (!response.ok) {
      const message = data?.message || data?.error || `HTTP ${response.status}`;
      throw new Error(`CollegeFootballData schedule failed: ${message}`);
    }

    const schedule = Array.isArray(data?.schedule) ? data.schedule : [];
    if (schedule.length === 0) {
      throw new Error(`CollegeFootballData returned no ${season} games for ${teamName}`);
    }

    const result = {
      schedule,
      sport: 'College Football',
      provider: 'CollegeFootballData',
      season: data?.season || season,
    };

    putCached(key, result);
    return result;
  }

  static async getUpcomingGames(teamName) {
    const full = await this.getTeamSchedule(teamName);
    if (!full) return null;

    const today = todayEastern();
    const upcoming = full.schedule.filter(game => game.date >= today && !game.isCompleted);

    // The trip planner should expose every remaining game in the season, not
    // only an arbitrary first six. Keep the complete season separately too so
    // past/final games can still be shown with the existing full-season toggle.
    return {
      ...full,
      schedule: upcoming,
      fullSchedule: full.schedule,
    };
  }

  static clearCache() {
    cache.clear();
  }

  static getCacheSize() {
    return cache.size;
  }
}

export default CFBDScheduleAPI;
